import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";

import { FooterNav } from "../../data/Nav";
import { AREAS } from "../../data/areaPages";

import { Container } from "../styles/Layout";
import { size } from "../styles/Breakpoints";

import GoogleReviews from "./GoogleReviews";

import { ReactComponent as LogoLight } from "../../assets/img/pp-logo-light.svg";
import { LaunchRounded, Facebook, LinkedIn, Instagram, YouTube } from "@material-ui/icons";
import { CONTACT_LINKS } from "../../data/contactLinks";

/*
 * @material-ui/icons (this project's icon set - see PictureAsPdf/LaunchRounded
 * above) has Facebook, LinkedIn, Instagram and YouTube built in, but predates
 * both TikTok's rise and Twitter's 2023 rebrand to X, so those two are drawn
 * by hand here instead. Both follow the same convention the library's own
 * icons use - a bare 24x24 viewBox, fill="currentColor", no hardcoded size -
 * so SocialIconsCol's `& svg` rule (width, hover colour) styles them exactly
 * like the rest of the row without any special-casing.
 */
function XIcon(props) {
	return (
		<svg viewBox="0 0 24 24" fill="currentColor" {...props}>
			<path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
		</svg>
	);
}
function TikTokIcon(props) {
	return (
		<svg viewBox="0 0 24 24" fill="currentColor" {...props}>
			<path d="M16.6 5.82c-1.006-.98-1.6-2.34-1.6-3.82h-3.44v13.56c0 1.71-1.39 3.12-3.12 3.12a3.12 3.12 0 0 1 0-6.24c.31 0 .61.05.88.14V9.09a6.6 6.6 0 0 0-.88-.06A6.58 6.58 0 0 0 2 15.61a6.58 6.58 0 0 0 6.44 6.58 6.58 6.58 0 0 0 6.56-6.58V9.19a9.03 9.03 0 0 0 5.28 1.71V7.46a5.64 5.64 0 0 1-3.68-1.64Z" />
		</svg>
	);
}

const Wrapper = styled.footer`
	background: white;
	margin-top: -0.5rem;
	border-top: 2px solid ${(props) => props.theme.darkblue};
	@media print {
		display: none;
	}
`;
const LowerSectionWrap = styled.div`
	background: ${(props) => props.theme.darkgradient};
`;
const LowerSection = styled.div`
	display: grid;
	grid-template-columns: 3.25fr 1fr 2fr 1fr;
	min-height: 13rem;
	color: white;
	@media screen and (max-width: ${size.sm}px) {
		display: block;
		height: 100%;
		padding: 1rem;
	}
`;
const AddressCol = styled.div`
	margin-top: 2rem;
`;
const Address = styled.div`
	font-size: 0.8rem;
	font-weight: 400;
	line-height: 1.5rem;
`;

/* Contact details (and their icons and link targets) live in data/contactLinks.js. */
const ContactLink = styled.a`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	color: inherit;
	transition: color 0.3s;
	&:hover,
	&:focus-visible {
		color: ${(props) => props.theme.lightblue};
	}
	& svg {
		flex: none;
		width: 1rem;
		height: 1rem;
	}
`;
const Copyright = styled.div`
	font-size: 0.8rem;
	margin-bottom: 4rem;
	color: ${(props) => props.theme.lightblue};
`;
const MenuCol = styled.ul`
	list-style: none;
	padding: 0;
	margin-top: 2rem;
	@media screen and (max-width: ${size.sm}px) {
		display: inline-block;
		margin-right: 2rem;
	}
`;
const MenuTitle = styled.li`
	line-height: 1.5rem;
	font-size: 0.8rem;
	font-weight: 500;
	&:hover {
		color: ${(props) => props.theme.lightblue};
		transition: 0.3s;
	}
`;
const MenuItem = styled.li`
	line-height: 1.5rem;
	font-size: 0.8rem;
	& svg {
		width: 0.8rem;
		margin-bottom: -0.5rem;
		margin-left: 0.5rem;
		opacity: 0.5;
	}
	&:hover {
		color: ${(props) => props.theme.lightblue};
		transition: 0.3s;
	}
`;
/*
 * Six icons in a column that's only 1fr of a 3.25fr/1fr/2fr/1fr grid - on
 * some widths that's not wide enough for them in a row without wrapping
 * onto a ragged second line. Stacked vertically instead, so it never has
 * to fit six icons' worth of width - only the tightest column's width
 * gets used, at any viewport wide enough to be on the grid layout at all.
 * Mobile (LowerSection collapses to display:block, full page width) goes
 * back to a row - six icons is no problem for a full-width column.
 */
const SocialIconsCol = styled.div`
	margin-top: 2rem;
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 0.6rem;
	& svg {
		width: 2rem;
		height: 2rem;
	}
	& svg:hover {
		color: ${(props) => props.theme.lightblue};
		transition: 0.3s;
	}
	@media screen and (max-width: ${size.sm}px) {
		flex-direction: row;
		flex-wrap: wrap;
		align-items: center;
	}
`;
const Logo = styled(LogoLight)`
	width: 70%;
	margin-bottom: 1rem;
`;

/*
 * Sits below the footer grid, full width - like AreaStrip below it. The
 * previous version of this (FooterLogosWrap, now gone) lived inside the
 * narrow Copyright column and only had room for four square logos in a
 * row; these seven, in two labelled groups, need the width.
 */
const TrustBar = styled.div`
	border-top: 1px solid rgba(255, 255, 255, 0.15);
	padding: 1.5rem 0;
	display: flex;
	flex-wrap: wrap;
	gap: 1.5rem 3rem;
	@media screen and (max-width: ${size.sm}px) {
		padding: 1.5rem 1rem;
	}
`;
const TrustGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.6rem;
`;
const TrustLabel = styled.div`
	font-size: 0.7rem;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	opacity: 0.6;
	color: white;
`;
const TrustLogos = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.75rem;
`;
const TrustLogoLink = styled.a`
	display: flex;
	align-items: center;
	height: 2.4rem;
	background: white;
	padding: 0.35rem 0.7rem;
	border-radius: 4px;
	transition: 0.3s;
	&:hover {
		transform: translateY(-2px);
		transition: 0.3s;
	}
`;
const TrustLogoImg = styled.img`
	height: 100%;
	width: auto;
	display: block;
`;

/*
 * A tile that carries wording instead of a logo, for a body whose logo we
 * are not entitled to display. Same white tile, same height, so it sits in
 * the row without looking like a broken image.
 *
 * The ICO is the case this exists for. Their copyright terms are explicit:
 * "Use of any of the Commissioner's logos without the Commissioner's consent
 * infringes the copyright held in respect of the logos", and consent is
 * given only where the ICO has approved the material it appears on - because
 * their logo is meant to signify that the ICO prepared or approved the thing
 * it sits on, which is not what a registration is. The registration itself
 * is public, so the reference number says the same thing lawfully.
 */
const TrustTextLink = styled(TrustLogoLink)`
	flex-direction: column;
	align-items: flex-start;
	justify-content: center;
	line-height: 1.1;
	color: ${(props) => props.theme.darkblue};
	padding: 0.3rem 0.75rem;
`;
const TrustTextTop = styled.span`
	font-size: 0.52rem;
	font-weight: 600;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	opacity: 0.7;
`;
const TrustTextMain = styled.span`
	font-size: 0.82rem;
	font-weight: 700;
	letter-spacing: 0.02em;
`;

/*
 * Same organisations, same links, as the trust bar on the sales/lettings
 * guides (front/src/data/guides/*Content.js) - kept as one literal list
 * rather than a shared import because one copy is plain JSX here and the
 * other is a raw HTML string; a shared data source would still need two
 * different rendering paths either way.
 */
const LISTED_ON = [
	{ name: "Rightmove", href: "https://www.rightmove.co.uk/estate-agents/agent/Property-People/London-290249.html", logo: "/images/guides/logo-rightmove.png" },
	{ name: "Zoopla", href: "https://www.zoopla.co.uk/find-agents/branch/property-people-london-32433/", logo: "/images/guides/logo-zoopla.png" },
	{ name: "OnTheMarket", href: "https://www.onthemarket.com/agents/branch/property-people-tooting/", logo: "/images/guides/logo-onthemarket.png" },
];
const MEMBERS_OF = [
	{ name: "The Property Ombudsman", href: "https://www.tpos.co.uk/business-search/property-people/", logo: "/images/guides/logo-tpos.png" },
	{ name: "Propertymark Client Money Protection", href: "/client-money-protection", logo: "/images/guides/logo-cmp.png", internal: true },
	{ name: "Propertymark", href: "https://www.propertymark.co.uk/company/property-people-london-ltd.html", logo: "/images/guides/logo-propertymark.png" },
	{ name: "Deposit Protection Service", href: "https://www.depositprotection.com/", logo: "/images/guides/logo-dps.png" },
	// Wording rather than a logo - see TrustTextLink above for why.
	{
		name: "Registered with the Information Commissioner's Office, reference ZA005338",
		href: "/ico-registration",
		internal: true,
		text: { top: "ICO registered", main: "ZA005338" },
	},
];

/*
 * Sits below the footer grid rather than inside it - LowerSection is a fixed
 * four-column layout and adding a fifth would break it.
 *
 * This puts a link to every area page on every page of the site. Without it
 * the area pages would exist only in sitemap.xml, with nothing linking to
 * them, which is a weak signal and slow to get crawled.
 */
const AreaStrip = styled.div`
	border-top: 1px solid rgba(255, 255, 255, 0.15);
	padding: 1.25rem 0 2rem 0;
	color: white;
	& a {
		display: inline-block;
		font-size: 0.8rem;
		font-weight: 400;
		margin: 0 1.1rem 0.4rem 0;
		opacity: 0.85;
	}
	& a:hover {
		opacity: 1;
		text-decoration: underline;
	}
	@media screen and (max-width: ${size.sm}px) {
		padding: 1.25rem 1rem 2rem 1rem;
	}
`;

const AreaStripTitle = styled.div`
	font-size: 0.75rem;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	opacity: 0.6;
	margin-bottom: 0.6rem;
`;

export default function Footer() {
	return (
		<Wrapper>
			{/* <GoogleReviews /> */}
			<LowerSectionWrap>
				<Container>
					<LowerSection>
						<AddressCol>
							<Logo />
							{CONTACT_LINKS.map(({ icon: Icon, text, href, label, external }) => (
								<Address key={href}>
									<ContactLink
										href={href}
										aria-label={label}
										{...(external && { target: "_blank", rel: "noopener noreferrer" })}
									>
										<Icon aria-hidden="true" />
										{text}
									</ContactLink>
								</Address>
							))}
						</AddressCol>
						{FooterNav.map((item, index) => (
							<MenuCol key={index}>
								<MenuTitle>
									<Link to={item.link}>{item.title}</Link>
								</MenuTitle>
								{item.submenu &&
									item.submenu.map((subitem, subindex) => (
										<MenuItem key={subindex}>
											{/* Check if it's an external or internal link first */}
											{/^https?:\/\//.test(subitem.link) ? (
												<a href={subitem.link} target={subitem.target}>
													{subitem.title}
													{subitem.title === "INSTANT VALUATION" && <LaunchRounded />}
													{subitem.title === "Refurbishment" && <LaunchRounded />}
													{subitem.title === "Cleaning" && <LaunchRounded />}
												</a>
											) : (
												<Link to={subitem.link} target={subitem.target}>
													{subitem.title}
													{subitem.title === "INSTANT VALUATION" && <LaunchRounded />}
													{subitem.title === "Refurbishment" && <LaunchRounded />}
													{subitem.title === "Cleaning" && <LaunchRounded />}
												</Link>
											)}
										</MenuItem>
									))}
							</MenuCol>
						))}
						<SocialIconsCol>
							<a href="https://x.com/Property_People" target="_blank" rel="noreferrer">
								<XIcon />
							</a>
							<a
								href="https://www.instagram.com/propertypeoplelondon/"
								target="_blank"
								rel="noreferrer"
							>
								<Instagram />
							</a>
							<a href="https://www.tiktok.com/@propertypeoplelondon" target="_blank" rel="noreferrer">
								<TikTokIcon />
							</a>
							<a
								href="https://www.youtube.com/@PropertyPeopleLondon"
								target="_blank"
								rel="noreferrer"
							>
								<YouTube />
							</a>
							<a
								href="https://www.facebook.com/Property.People.London"
								target="_blank"
								rel="noreferrer"
							>
								<Facebook />
							</a>
							<a
								href="https://www.linkedin.com/company/property-people-london"
								target="_blank"
								rel="noreferrer"
							>
								<LinkedIn />
							</a>
						</SocialIconsCol>
						<Copyright>
							© {new Date().getFullYear()} Property People London Ltd.
							<br /> Property People is a trading name of Property People London Ltd (Reg No:
							07355433)
						</Copyright>
					</LowerSection>
					<TrustBar>
						<TrustGroup>
							<TrustLabel>Listed on</TrustLabel>
							<TrustLogos>
								{LISTED_ON.map((item) => (
									<TrustLogoLink key={item.name} href={item.href} target="_blank" rel="noreferrer">
										<TrustLogoImg src={item.logo} alt={item.name} />
									</TrustLogoLink>
								))}
							</TrustLogos>
						</TrustGroup>
						<TrustGroup>
							<TrustLabel>Members of</TrustLabel>
							<TrustLogos>
								{MEMBERS_OF.map((item) =>
									item.text ? (
										<TrustTextLink key={item.name} as={Link} to={item.href} title={item.name}>
											<TrustTextTop>{item.text.top}</TrustTextTop>
											<TrustTextMain>{item.text.main}</TrustTextMain>
										</TrustTextLink>
									) : item.internal ? (
										<TrustLogoLink key={item.name} as={Link} to={item.href}>
											<TrustLogoImg src={item.logo} alt={item.name} />
										</TrustLogoLink>
									) : (
										<TrustLogoLink key={item.name} href={item.href} target="_blank" rel="noreferrer">
											<TrustLogoImg src={item.logo} alt={item.name} />
										</TrustLogoLink>
									)
								)}
							</TrustLogos>
						</TrustGroup>
					</TrustBar>
					<AreaStrip>
						<AreaStripTitle>Areas we cover</AreaStripTitle>
						{AREAS.map((area) => (
							<Link key={area.slug} to={`/estate-agents/${area.slug}`}>
								{area.name}
							</Link>
						))}
					</AreaStrip>
				</Container>
			</LowerSectionWrap>
		</Wrapper>
	);
}
